<template>
	<div class="xs-12">
		<!-- Entities Switcher -->
		<div v-if="!showEntityPickerCreate" class="xs-12">
			<div class="flex xs-12 bb1 bc7">
				<div v-for="(tab, i) in tabs" :key="i">
					<div
						v-if="allowedEntityTypes.includes(tab.id)"
						@click="selectEntityType(tab)"
						class="cu2 c1 fw7 p4 bb2 bc0"
						:class="{ 'bc2 c2': tab.id === selectedEntityType }"
					>
						{{ tab.label }}
					</div>
				</div>
			</div>
		</div>

		<!-- Body -->
		<div v-if="!showEntityPickerCreate" class="p4 mt2">
			<!-- Seacrh -->
			<!-- <div class="fw7 lh125 mb1">Search {{ selectedEntityType }}</div> -->
			<j-base-input-search
				v-model="searchTerm"
				@input="onSearch"
				:placeholder="'Search ' + selectedEntityType"
			/>

			<!-- Loading -->
			<div class="xs-12 flex xs-center p2">
				<j-spinner :delay="300" v-if="isSearchLoading" />
			</div>

			<!-- List of entities -->
			<div v-if="computedEntitiesOptions.length">
				<div class="mt4 fw7 lh125 c6 mb1">
					{{ $t("select") }} {{ selectedEntityType }}:
				</div>
				<div
					v-for="entity in computedEntitiesOptions"
					:key="entity.id"
					class="r2 p2 mb2 cu2 bg5"
					:class="[
						{ 'bc2 b2': entity.isSelected },
						{ 'bc7 b1': !entity.isSelected }
					]"
					@click="
						$emit('entity:selected', { id: entity.id, name: entity.name })
					"
				>
					<div class="xs-12">
						<div class="c6 fw7 lh125 fs3">{{ $t("name") }}:</div>
						<div class="lh125 xs-12 fw7">{{ entity.name }}</div>
					</div>
					<div class="xs-12 mt2">
						<div class="c6 fw7 lh125 fs3">{{ $t("internal_code") }}:</div>
						<div class="lh125 xs-12">{{ entity.internalCode }}</div>
					</div>
				</div>
			</div>

			<!-- No data -->
			<div
				v-if="
					!isSearchLoading &&
						isSearchTouched &&
						computedEntitiesOptions.length === 0
				"
				class="xs-12 p4 ta2 fs3 c6 fw7 mb4"
			>
				{{ $t("errors.no_data") }} :(
			</div>

			<!-- Add new button -->
			<div class="flex xs-12 xs-right">
				<j-button
					type="bordered"
					size="small"
					@click="showEntityPickerCreate = true"
				>
					{{ $t("create_new") }} {{ selectedEntityType }}
				</j-button>
			</div>
		</div>

		<!-- Add new modal -->
		<!--
		<core-modal
			name="entityPickerCreate"
			:headline="'Create ' + selectedEntityType"
			:pivotX="0"
			:pivotY="0"
			:hasHeader="false"
		>
			<div style="height:100vh; overflow: scroll;">
				<entity-picker-create
					:entityType="selectedEntityType"
					@entity:created="onEntityCreated"
					@cancel="$modal.hide('entityPickerCreate')"
				/>
			</div>
		</core-modal>
		-->
		<div v-if="showEntityPickerCreate">
			<entity-picker-create
				:entityType="selectedEntityType"
				@entity:created="onEntityCreated"
				@cancel="showEntityPickerCreate = false"
			/>
		</div>
	</div>
</template>

<script>
/**
 * This component is responsible for searching entites
 * Once user clicks on particular entity, it emits event with payload
 * This component is also responsible for creation of new entities
 * @event entity:selected when entity is selected, it returns object {id: "entity id", name: "entity name"}
 */
import JButton from "@/components/JButton"
import JBaseInputSearch from "@/components/JBaseInputSearch"
import JSpinner from "@/components/JSpinner"
import EntityPickerCreate from "./EntityPickerCreate"
import CoreModal from "@/modules/core/components/CoreModal"

import debounce from "lodash.debounce"
import api from "@/api/gql.js"

export default {
	components: {
		JButton,
		JBaseInputSearch,
		JSpinner,
		EntityPickerCreate,
		CoreModal
	},

	props: {
		/**
		 * Value represents `id` of selected entity
		 * Bsed on this selected entity will be highlighted, it happens in computed
		 */
		value: {
			type: String
		},

		allowedEntityTypes: {
			type: Array,
			default: () => ["individual", "company", "object"] // individual, company, object, participant, organization
		}
	},

	data() {
		return {
			showEntityPickerCreate: false,
			// Tabs
			tabs: [
				{
					id: "individual",
					label: "Individual"
				},
				{
					id: "company",
					label: "Company"
				},
				{
					id: "object",
					label: "Object"
				},
				{
					id: "participant",
					label: "Participant"
				},
				{
					id: "organization",
					label: "Organization"
				}
			],

			// Entity to search
			selectedEntityType: "",
			isSearchLoading: false,
			searchTerm: "",
			isSearchTouched: false, // we keep track if we should show no data message

			// Fetched entities for selection
			options: { entities: [] },

			// Toggle form for creation
			isAdding: false
		}
	},

	computed: {
		computedEntitiesOptions() {
			return this.options?.entities?.map(item => ({
				...item,
				isSelected: item.id === this.value
			}))
		}
	},

	methods: {
		selectEntityType(tab) {
			this.selectedEntityType = tab.id
			this.searchTerm = "" // reset search term
			this.options.entities = [] // reset entities
			this.isSearchTouched = false // reset touched so that it dosn't show no data message
		},

		onSearch: debounce(function() {
			this.isSearchTouched = true
			this.fetchEntities(this.searchTerm)
		}, 300),

		/**
		 * search term will be passed from when user searches in select box
		 * id will be passed in watcher when value changes so that we can fetch proper name in select box
		 */
		async fetchEntities(searchTerm, id) {
			// Define api keys per each entity
			let apiKeys = { q: "individuals", w: "individualWhereInput" }
			if (this.selectedEntityType === "company")
				apiKeys = { q: "companies", w: "companyWhereInput" }
			if (this.selectedEntityType === "object")
				apiKeys = { q: "objects", w: "objectWhereInput" }
			if (this.selectedEntityType === "organization")
				apiKeys = { q: "organizations", w: "organizationWhereInput" }
			if (this.selectedEntityType === "participant")
				apiKeys = { q: "internals", w: "internalWhereInput" }

			// Universal query
			const query = `
				query ${apiKeys.q}Options($where: ${apiKeys.w}) {
					${apiKeys.q}(where: $where) {
						list {
							id
							name
							internalCode
						}
					}
				}
      `

			this.isSearchLoading = true
			const variables = {
				limit: 400, // limit results so that they have to make more precise search
				where: { name_contains: searchTerm, id }
			}
			const res = await api({ query, variables })
			this.isSearchLoading = false

			// Save
			this.options.entities = res?.data?.[apiKeys.q]?.list
		},

		onEntityCreated({ id, name }) {
			// When entity was created, emit event up to select this entity
			this.$emit("entity:selected", { id, name })
		}
	},

	created() {
		this.selectedEntityType = this.tabs?.filter(tab =>
			this.allowedEntityTypes.includes(tab.id)
		)?.[0]?.id
	}
}
</script>
